import { AutomationAction } from './ast/AutomationAction.js';
import { toHomeAssistantAction } from './haActionYaml.js';
import { renderTemplateMessage } from './renderTemplateDocument.js';
import {
  createTemplateDocument,
  createTextNode,
  createTtsTitle,
  parseSimpleTemplateDocument,
} from './templateDocument.js';

const THUMBNAIL = 'https://brands.home-assistant.io/_/cloud/logo.png';

export const TTS_LANGUAGES = [
  {
    value: 'nl-NL',
    label: 'Nederlands (Nederland)',
    voices: [
      { value: 'FennaNeural', label: 'Fenna' },
      { value: 'MaartenNeural', label: 'Maarten' },
    ],
  },
  {
    value: 'en-US',
    label: 'English (United States)',
    voices: [
      { value: 'JennyNeural', label: 'Jenny' },
      { value: 'GuyNeural', label: 'Guy' },
    ],
  },
];

export const DEFAULT_TTS_LANGUAGE = 'nl-NL';
export const DEFAULT_TTS_VOICE = 'FennaNeural';
export const DEFAULT_TTS_PROVIDER = 'cloud';

export function voicesForLanguage(language) {
  return TTS_LANGUAGES.find(option => option.value === language)?.voices ?? [];
}

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
  }
  return value;
}

export function buildMediaPlayerTtsAction({
  targetEntityId,
  entityId = targetEntityId,
  text,
  message,
  ttsProviderId,
  provider = ttsProviderId ?? DEFAULT_TTS_PROVIDER,
  language = DEFAULT_TTS_LANGUAGE,
  voice = DEFAULT_TTS_VOICE,
}) {
  const document = message ?? createTemplateDocument([createTextNode(text ?? '')]);
  const rendered = renderTemplateMessage(document);
  let mediaContentId;
  if (rendered.dynamic) {
    const messageParameter = `{{ (${rendered.expression}) | urlencode }}`;
    mediaContentId =
      `media-source://tts/${provider}` +
      `?message=${messageParameter}` +
      `&language=${encodeURIComponent(language)}` +
      `&voice=${encodeURIComponent(voice)}`;
  } else {
    const query = new URLSearchParams({
      message: rendered.text,
      language,
      voice,
    });
    mediaContentId = `media-source://tts/${provider}?${query.toString()}`;
  }

  return new AutomationAction({
    type: 'call_service',
    provider: 'media_player',
    service: 'play_media',
    target: { entity_id: entityId },
    data: {
      media: {
        media_content_id: mediaContentId,
        media_content_type: 'provider',
        metadata: {
          title: createTtsTitle(document),
          thumbnail: THUMBNAIL,
          media_class: 'app',
          children_media_class: null,
          navigateIds: [
            {},
            {
              media_content_type: 'app',
              media_content_id: 'media-source://tts',
            },
            {
              media_content_type: 'provider',
              media_content_id: mediaContentId,
            },
          ],
        },
      },
    },
    editorMetadata: {
      type: 'media_player',
      operation: 'tts',
      targetEntityId: entityId,
      ttsProviderId: provider,
      language,
      voice,
      message: document,
    },
  });
}

export const buildTtsAction = buildMediaPlayerTtsAction;

export function materializeTtsAction(action) {
  const metadata = action?.editorMetadata;
  if (metadata?.type !== 'media_player' || metadata.operation !== 'tts') return action;
  return buildMediaPlayerTtsAction(metadata);
}

export function parseTtsAction(action) {
  if (!action || action.provider !== 'media_player' || action.service !== 'play_media') return null;
  const entityId = action.target?.entity_id;
  if (typeof entityId !== 'string' || !entityId) return null;

  const media = action.data?.media;
  const mediaContentId = media?.media_content_id;
  if (typeof mediaContentId !== 'string' || !mediaContentId.startsWith('media-source://tts/')) return null;
  if (media.media_content_type !== 'provider') return null;

  let parsedUrl;
  try {
    parsedUrl = new URL(mediaContentId);
  } catch {
    return null;
  }
  const provider = decodeURIComponent(parsedUrl.pathname.replace(/^\//, ''));
  const query = parsedUrl.searchParams;
  if (!provider) return null;
  const text = query.get('message');
  const language = query.get('language');
  const voice = query.get('voice');
  if (text == null) return null;
  if (['message', 'language', 'voice'].some(key => query.getAll(key).length > 1)) return null;
  if ([...query.keys()].some(key => !['message', 'language', 'voice'].includes(key))) return null;
  if (!language || !voice) return null;

  const metadata = action.editorMetadata;
  let message;
  if (metadata?.type === 'media_player' && metadata.operation === 'tts' && metadata.message?.nodes) {
    message = clone(metadata.message);
  } else {
    message = parseSimpleTemplateDocument(text);
    if (!message) return null;
  }

  const expected = toHomeAssistantAction(buildTtsAction({ entityId, message, provider, language, voice }));
  const actual = clone(toHomeAssistantAction(action));
  // The primary URL is authoritative. Normalize URL ordering and the duplicate
  // metadata URL before comparing the rest of the payload losslessly.
  actual.data.media.media_content_id = expected.data.media.media_content_id;
  const providerItem = actual.data.media.metadata?.navigateIds?.[2];
  if (providerItem?.media_content_type !== 'provider') return null;
  providerItem.media_content_id = expected.data.media.media_content_id;
  if (JSON.stringify(actual) !== JSON.stringify(expected)) return null;
  return { entityId, operation: 'tts', message, provider, language, voice };
}

export function canRepresentAsFriendlyTts(action) {
  return parseTtsAction(action) !== null;
}
